import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Info } from "lucide-react";

const ToothIcon = ({ className = "h-9 w-9", color }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="M12 3.5c-1.2-1-2.8-1.6-4.3-1.4C5 2.5 3.1 4.6 3.1 7.2c0 1.9.8 3.4 1.6 5 .7 1.4 1.1 2.8 1.1 4.3 0 2.7 1.2 5 2.7 5 1.7 0 1.9-2.6 1.9-5.2 0-.5.5-1 1.1-1s1.1.5 1.1 1c0 2.6.2 5.2 1.9 5.2 1.5 0 2.7-2.3 2.7-5 0-1.5.4-2.9 1.1-4.3.8-1.6 1.6-3.1 1.6-5 0-2.6-1.9-4.7-4.6-5.1-1.5-.2-3.1.4-4.3 1.4z" />
  </svg>
);
import { OPERATORS_NATIONAL, OPERATORS_REGIONAL, DENTAL, waLink } from "../../data/site";
import { Reveal } from "./Reveal";

const TABS = [
  { id: "nacionais", label: "Nacionais", data: OPERATORS_NATIONAL },
  { id: "regionais", label: "Regionais", data: OPERATORS_REGIONAL },
  { id: "odontologicos", label: "Odontológicos", data: DENTAL },
];

const LogoCard = ({ op, i }) => (
  <motion.div
    layout
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -12 }}
    transition={{ duration: 0.45, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
    data-testid={`operator-card-${op.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    className="group relative flex h-40 flex-col items-center justify-center overflow-hidden rounded-2xl border border-white/5 bg-surface px-4 text-center transition-[border-color,box-shadow,transform] duration-500 hover:-translate-y-1 hover:border-champagne/30 hover:shadow-[0_18px_44px_-20px_rgba(0,0,0,0.7)]"
    style={{ "--brand": op.color }}
  >
    <span className="absolute left-0 top-0 h-full w-1 bg-[var(--brand)] opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
    <span
      className={`flex h-16 w-full max-w-[190px] items-center justify-center rounded-xl px-3 transition-transform duration-500 group-hover:scale-105 ${
        op.dark ? "bg-graphite" : "bg-white"
      }`}
    >
      <img
        src={op.logo}
        alt={`Logo ${op.name}`}
        className={op.tile ? "h-12 w-12 rounded-md object-cover" : "h-10 w-auto max-w-[150px] object-contain"}
      />
    </span>
    <span className="mt-3 text-xs font-semibold text-stone-400 transition-colors duration-500 group-hover:text-ink">
      {op.name}
    </span>
  </motion.div>
);

const DentalCard = ({ op, i }) => (
  <motion.div
    layout
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -12 }}
    transition={{ duration: 0.45, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
    data-testid={`operator-card-${op.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    className="group relative flex h-40 flex-col items-center justify-center overflow-hidden rounded-2xl border border-white/5 bg-surface px-4 text-center transition-[border-color,box-shadow,transform] duration-500 hover:-translate-y-1 hover:border-champagne/30 hover:shadow-[0_18px_44px_-20px_rgba(0,0,0,0.7)]"
    style={{ "--brand": op.color }}
  >
    <span className="absolute left-0 top-0 h-full w-1 bg-[var(--brand)] opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
    <span className="flex h-16 w-full max-w-[190px] items-center justify-center rounded-xl bg-graphite px-4 transition-transform duration-500 group-hover:scale-105">
      <motion.span
        animate={{ rotate: [0, -12, 0, 8, 0] }}
        transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut", delay: i * 0.5 }}
        className="flex items-center justify-center"
      >
        <ToothIcon className="h-9 w-9" color={op.color} />
      </motion.span>
    </span>
    <span className="mt-3 text-xs font-semibold text-stone-400 transition-colors duration-500 group-hover:text-[var(--brand)]">
      {op.name}
    </span>
  </motion.div>
);

export const Operators = () => {
  const [tab, setTab] = useState("nacionais");
  const active = TABS.find((t) => t.id === tab);
  const isDental = tab === "odontologicos";

  return (
    <section id="operadoras" data-testid="operators-section" className="bg-paper py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-end">
          <div>
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.24em] text-champagne-dark">
              Operadoras
            </p>
            <h2 className="mt-4 max-w-xl font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl lg:text-5xl">
              Principais operadoras nacionais e regionais
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-stone-400 sm:text-lg">
              Opções regionais e nacionais para você escolher de acordo com sua
              necessidade e localização.
            </p>
          </div>
          <div className="flex rounded-full border border-white/10 bg-surface p-1" data-testid="operators-tabs">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`operators-tab-${t.id}`}
                className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-[background-color,color] duration-300 ${
                  tab === t.id ? "bg-brand text-white" : "text-stone-400 hover:text-ink"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-12 min-h-[320px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={tab}
              className="grid grid-cols-2 gap-4 sm:grid-cols-3"
            >
              {active.data.map((op, i) =>
                isDental || !op.logo ? (
                  <DentalCard key={op.name} op={op} i={i} />
                ) : (
                  <LogoCard key={op.name} op={op} i={i} />
                )
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {isDental && (
          <Reveal className="mt-8 flex flex-col items-start justify-between gap-4 rounded-2xl border border-white/5 bg-surface p-6 sm:flex-row sm:items-center">
            <p className="text-sm text-stone-400">
              Entre outras opções disponíveis de acordo com o perfil e a região.
            </p>
            <a
              href={waLink("Olá! Quero conhecer as opções de planos odontológicos.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="dental-cta"
              className="shrink-0 rounded-full bg-brand px-6 py-3 text-sm font-bold text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-brand-dark"
            >
              Quero conhecer as opções odontológicas
            </a>
          </Reveal>
        )}

        <Reveal className="mt-10 flex items-start gap-3 text-xs leading-relaxed text-stone-500">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            Trabalho com planos de saúde regulamentados pela ANS, de diversas
            operadoras. A disponibilidade, rede credenciada, valores, condições
            comerciais e elegibilidade podem variar conforme região, idade,
            modalidade e perfil de contratação.
          </p>
        </Reveal>
      </div>
    </section>
  );
};
